/**
 * This is an unpublished work containing 3M confidential and proprietary information.
 * Disclosure, use or reproduction without the written authorization of 3M is prohibited.
 * If publication occurs, the following notice applies:
 * Copyright (C) 2025, 3M. All rights reserved.
 */
import React, { useEffect, useRef, useState, RefObject } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useSelector, useDispatch } from "react-redux";
import { useTheme } from "../Theme/ThemeProvider";
import { Greeting } from "./Greeting";
import { ChatInput } from "../Chat/ChatInput";
import { Gallery } from "../PromptGallery/DefaultPrompts";
import { GalleryMode } from "../PromptGallery/data/galleryTypes";
import { LegalDisclaimers } from "./LegalDisclaimers";
import { KnowledgeRagSelector } from "./KnowledgeRagSelector";
import { RootState } from "../../store/store";
import { isModelSelectorVisible } from "../../utils/modelConfig";
import ModelSelector from "../Chat/ModelSelector";
import logger from "../../utils/logger";
import { clearPendingPrompt } from "../../store/chatModeSlice";

interface DesktopLayoutProps {
  galleryState: {
    current: { mode: GalleryMode | null; direction: number };
    goToModeWithDirection: (mode: GalleryMode | null, direction: number) => void;
    exitDirectionRef: RefObject<number>;
    isAnimating: boolean;
    setIsAnimating: (value: boolean) => void;
    setExitDirectionRef: RefObject<((direction: number) => void) | null>;
    isFirstAnimationRunning: boolean;
    setIsFirstAnimationRunning: (value: boolean) => void;
  };
  onNavigateToChat?: (message: string, tools?: string[]) => void;
  showPromptLibrary?: boolean;
  onClosePromptLibrary?: () => void;
}

export function DesktopLayout({ galleryState, onNavigateToChat, showPromptLibrary, onClosePromptLibrary }: DesktopLayoutProps) {
  const { mode, theme } = useTheme();
  const chatModeState = useSelector((state: RootState) => state.chatMode);
  const voiceModeState = useSelector((state: RootState) => state.voiceMode);
  const dispatch = useDispatch();
  const [chatInputHeight, setChatInputHeight] = useState(56);
  // Track whether we're above the 1024px threshold used for the top-bar ModelSelector
  const [isWideScreen, setIsWideScreen] = useState(() => typeof window !== 'undefined' ? window.innerWidth >= 1024 : true);
  useEffect(() => {
    const handleResize = () => setIsWideScreen(window.innerWidth >= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Debug logging for height changes
  const handleHeightChange = (height: number) => {
    setChatInputHeight(height);
  };
  
  const [chatInputExternalValue, setChatInputExternalValue] = useState<string | undefined>(undefined);

  // Consume pendingPrompt from Redux (dispatched by Prompt Library)
  useEffect(() => {
    if (chatModeState.pendingPrompt) {
      setChatInputExternalValue(chatModeState.pendingPrompt.text);
      const tool = chatModeState.pendingPrompt.tool;
      if (tool !== undefined) {
        window.dispatchEvent(new CustomEvent('galleryToolSelection', { detail: { tool } }));
      }
      dispatch(clearPendingPrompt());
    }
  }, [chatModeState.pendingPrompt, dispatch]);

  // Get accent color from theme
  const accent = theme.replace('dark-', '').replace('light-', '') as 'blue' | 'green' | 'purple' | 'none';

  // Handle tile click from gallery
  const handleTileClick = (prompt: string, tool?: string) => {
    logger.log('[DesktopLayout] Tile clicked with prompt:', prompt, 'and tool:', tool);
    setChatInputExternalValue(prompt);
    // Trigger tool selection by dispatching a custom event
    // Always dispatch if tool is defined (including 'none' to clear tools)
    if (tool !== undefined) {
      logger.log('[DesktopLayout] Dispatching galleryToolSelection event with tool:', tool);
      window.dispatchEvent(new CustomEvent('galleryToolSelection', { detail: { tool } }));
    }
  };

  // Reset external value after it's been applied
  const handleExternalValueApplied = () => {
    setChatInputExternalValue(undefined);
  };

  // Show indicator only briefly after mode/accent change
  const [showIndicator, setShowIndicator] = useState(false);
  const prevTheme = useRef(theme);
  useEffect(() => {
    if (prevTheme.current !== theme) {
      setShowIndicator(true);
      prevTheme.current = theme;
      const timeout = setTimeout(() => setShowIndicator(false), 4000);
      return () => clearTimeout(timeout);
    }
  }, [theme]);

  // Responsive horizontal padding strategy: minimize margins early (0→1→2→6) then restore wider spacing progressively.
  const sharedPaddingClass = "px-0 sm:px-1 md:px-2 lg:px-6 xl:px-[50px] 2xl:px-[100px] 3xl:px-[140px]";
  const topMargin = window.innerWidth < 1400 ? '6vh' : '10vh';

  return (
    <div className="flex flex-col h-full relative">
      
        <>
          {/* Dark vs light mode indicator plus accent (flashes briefly when it changes) */}
          {showIndicator && (
            <div className="absolute top-0 right-0 bg-black/50 text-white p-1 text-xs transition-opacity duration-500 z-30">
              {mode} mode | {accent === 'none' ? 'no' : accent} accent
            </div>
          )}

          {/* Model selector - top right (desktop only) */}
          {/* Only show if not in '3M Knowledge' mode, and only on desktop */}
          {typeof window !== 'undefined' && window.innerWidth >= 1024 && chatModeState.mode !== '3M Knowledge' && (
            <div className="fixed flex justify-end pointer-events-none" style={{ zIndex: 101, top: '88px', right: '16px' }}>
              <ModelSelector visible={isModelSelectorVisible(chatModeState.selectedTool, voiceModeState.isActive)} />
            </div>
          )}

          {/* ── Always-visible static header: Greeting + Legal ── */}
          <div
            className={`flex flex-col w-full z-20 ${sharedPaddingClass} mt-auto`}
            style={{ marginTop: topMargin }}
          >
            <Greeting overrideText={chatModeState.mode === '3M Knowledge' && chatModeState.ragChatType === null ? 'Welcome to 3M Knowledge!' : undefined} />
            <div className="mb-4">
              <LegalDisclaimers showNote={false} />
            </div>
          </div>

          {/* ── Animated lower content — only this part transitions ── */}
          <AnimatePresence mode="wait">
            {chatModeState.mode === '3M Knowledge' && chatModeState.ragChatType === null ? (
               /* NULL STATE: RAG selector — flex-1 passes height down so the gallery div itself can scroll */
              <motion.div
                key="rag-selector"
                initial={{ opacity: 1 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="flex-1 min-h-0 flex flex-col"
              >
                <KnowledgeRagSelector sharedPaddingClass={sharedPaddingClass} />
              </motion.div>
            ) : (
              /* NORMAL STATE: ChatInput + Gallery */
              <motion.div
                key="main-content"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="flex flex-col flex-1 min-h-0"
              >
                <div className={`flex flex-col w-full z-30 ${sharedPaddingClass}`}>
                  <ChatInput
                    onHeightChange={handleHeightChange}
                    onNavigateToChat={onNavigateToChat}
                    externalValue={chatInputExternalValue}
                    onExternalValueApplied={handleExternalValueApplied}
                    isWideScreen={isWideScreen}
                  />
                </div>

                {/* Gallery fills remaining space */}
                <AnimatePresence mode="wait">
                  {chatModeState.selectedTool !== 'translation' && (
                    <motion.div
                      key="gallery"
                      initial={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                      className={`flex-1 min-h-0 w-full mt-6 mb-6 z-20 ${sharedPaddingClass}`}
                    >
                      <Gallery
                          deviceType={'desktop'}
                          goToModeWithDirection={galleryState.goToModeWithDirection}
                          current={galleryState.current}
                          exitDirectionRef={galleryState.exitDirectionRef}
                          isAnimating={galleryState.isAnimating}
                          setIsAnimating={galleryState.setIsAnimating}
                          setExitDirectionRef={galleryState.setExitDirectionRef}
                          chatInputHeight={chatInputHeight}
                          onTileClick={handleTileClick}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
        </>
    </div>
  );
}